import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { Bell, ArrowLeft, CheckCheck, Filter, Loader2, AlertTriangle, Wrench, DollarSign, FileText, Mail, Send } from 'lucide-react';
import { toast } from 'sonner';

const TYPE_META = {
  repair: { label: 'Repair', icon: Wrench, color: '#F59E0B' },
  invoice: { label: 'Invoice', icon: DollarSign, color: '#22C55E' },
  intake: { label: 'Intake', icon: FileText, color: '#3B82F6' },
  approval: { label: 'Approval', icon: CheckCheck, color: '#A855F7' },
  alert: { label: 'Alert', icon: AlertTriangle, color: '#EF4444' },
  message: { label: 'Message', icon: Mail, color: '#06B6D4' },
};

export default function NotificationsInbox() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | unread | repair | invoice | intake | approval | alert
  const [markingAll, setMarkingAll] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (filter === 'unread') params.unread_only = true;
      const r = await api.get('/notifications', { params });
      setItems(r.data.notifications || []);
    } catch (e) {
      toast.error('Failed to load notifications');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);

  const markAllRead = async () => {
    setMarkingAll(true);
    try {
      await api.post('/notifications/read-all');
      toast.success('All marked as read');
      await load();
    } catch { toast.error('Failed'); }
    finally { setMarkingAll(false); }
  };

  const markOne = async (id) => {
    try { await api.post(`/notifications/${id}/read`); await load(); } catch {}
  };

  const filtered = filter === 'all' || filter === 'unread'
    ? items
    : items.filter(n => (n.type || '').includes(filter));

  const fmtTime = (ts) => {
    const d = new Date(ts);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff/86400)}d ago`;
    return d.toLocaleDateString();
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-[70%] mx-auto" data-testid="notifications-inbox">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded hover:bg-white/5"
            style={{ color: 'var(--text-muted)' }} data-testid="back-btn">
            <ArrowLeft size={18} />
          </button>
          <Bell size={20} style={{ color: 'var(--primary)' }} />
          <div>
            <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em]" style={{ color: 'var(--text-faint)' }}>// NOTIFICATIONS</p>
            <h1 className="font-['Manrope'] font-bold text-2xl" style={{ color: 'var(--text-primary)' }}>Inbox</h1>
          </div>
        </div>
        <button onClick={markAllRead} disabled={markingAll || items.every(n => n.is_read)}
          className="px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wide border transition-colors disabled:opacity-50 flex items-center gap-2"
          style={{ borderColor: 'var(--border)', color: 'var(--text-primary)' }}
          onMouseEnter={e => { if (!e.currentTarget.disabled) { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.color = 'var(--primary)'; } }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
          data-testid="mark-all-read-btn">
          {markingAll ? <Loader2 size={14} className="animate-spin" /> : <CheckCheck size={14} />}
          Mark all read
        </button>
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter size={13} style={{ color: 'var(--text-faint)' }} />
        {['all', 'unread', 'repair', 'invoice', 'intake', 'approval', 'alert'].map(f => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
            className={`px-3 py-1.5 rounded-full text-xs font-['IBM_Plex_Sans'] transition-colors border ${filter === f ? 'font-bold' : ''}`}
            style={{
              background: filter === f ? 'var(--primary)' : 'transparent',
              color: filter === f ? '#1A120E' : 'var(--text-muted)',
              borderColor: filter === f ? 'var(--primary)' : 'var(--border)',
            }}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--primary)' }} /></div>
      ) : filtered.length === 0 ? (
        <div className="card-slate p-12 text-center">
          <Bell size={32} className="mx-auto mb-3 opacity-40" style={{ color: 'var(--text-faint)' }} />
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No notifications {filter !== 'all' && `in "${filter}"`}</p>
        </div>
      ) : (
        <div className="card-slate divide-y" style={{ borderColor: 'var(--border)' }}>
          {filtered.map((n) => {
            const meta = TYPE_META[(n.type || '').split('_')[0]] || TYPE_META.message;
            const Icon = meta.icon;
            return (
              <div key={n.id} onClick={() => { if (!n.is_read) markOne(n.id); if (n.link) navigate(n.link); }}
                data-testid={`notification-${n.id}`}
                className={`p-4 flex items-start gap-3 transition-colors cursor-pointer hover:bg-white/5 ${!n.is_read ? 'border-l-2' : ''}`}
                style={{ borderColor: 'var(--border)', borderLeftColor: !n.is_read ? meta.color : 'transparent' }}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${meta.color}20`, border: `1px solid ${meta.color}40` }}>
                  <Icon size={14} style={{ color: meta.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className={`text-sm ${!n.is_read ? 'font-bold' : ''}`} style={{ color: 'var(--text-primary)' }}>{n.title || 'Notification'}</p>
                    <span className="text-[10px] flex-shrink-0" style={{ color: 'var(--text-faint)' }}>{fmtTime(n.created_at)}</span>
                  </div>
                  {n.message && <p className="text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>{n.message}</p>}
                  {n.type && (
                    <span className="inline-block mt-1.5 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm" style={{ background: 'var(--bg)', color: 'var(--text-faint)', border: '1px solid var(--border)' }}>
                      {n.type.replace(/_/g, ' ')}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
